import { LOCAL_CONFIG } from '../config/local.config';

// Mock API service for local development
export class MockApiService {
  private delay(ms: number = 500) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async getCurrentUser() {
    await this.delay(200);
    return LOCAL_CONFIG.MOCK_USER;
  }

  async getUserDetails(login: string) {
    await this.delay(200);
    return LOCAL_CONFIG.MOCK_USER;
  }

  async searchUser(query: string) {
    await this.delay(300);
    return [
      {
        label: 'Local User',
        value: 'local-user-123',
        icon: 'profile'
      },
      {
        label: 'Test User',
        value: 'test-user-456',
        icon: 'profile'
      }
    ].filter(user => user.label.toLowerCase().includes(query.toLowerCase()));
  }

  async searchGroup(value: string) {
    await this.delay(300);
    return [
      { label: 'local-team', value: 'local-team@example.com', icon: 'profile' },
      { label: 'dev-team', value: 'dev-team@example.com', icon: 'profile' }
    ];
  }

  async searchTeam(value: string) {
    await this.delay(300);
    return [
      { label: 'Development', value: 'Development', icon: 'profile' },
      { label: 'Frontend', value: 'Frontend', icon: 'profile' }
    ];
  }

  async getBookmarks(params: any = {}) {
    await this.delay(400);
    let bookmarks = [...LOCAL_CONFIG.MOCK_BOOKMARKS];
    
    // Apply filters if provided
    if (params.search) {
      bookmarks = bookmarks.filter(bookmark => 
        bookmark.title.toLowerCase().includes(params.search.toLowerCase()) ||
        bookmark.description.toLowerCase().includes(params.search.toLowerCase())
      );
    }
    
    if (params.tags) {
      bookmarks = bookmarks.filter(bookmark => 
        bookmark.tags.some(tag => params.tags.includes(tag))
      );
    }
    
    // Return in the format expected by FavoritesView - just the bookmarks array with favorite_bookmarks wrapper
    return {
      favorite_bookmarks: bookmarks
    };
  }

  async createBookmark(bookmarkData: any) {
    await this.delay(300);
    const newBookmark = {
      id: Date.now().toString(),
      ...bookmarkData,
      createdBy: LOCAL_CONFIG.MOCK_USER.login,
      createdAt: new Date().toISOString(),
      isFavorite: false
    };
    
    // In a real app, this would persist to storage
    LOCAL_CONFIG.MOCK_BOOKMARKS.push(newBookmark);
    return newBookmark;
  }

  async updateBookmark(id: string, bookmarkData: any) {
    await this.delay(300);
    const index = LOCAL_CONFIG.MOCK_BOOKMARKS.findIndex(b => b.id === id);
    if (index !== -1) {
      LOCAL_CONFIG.MOCK_BOOKMARKS[index] = {
        ...LOCAL_CONFIG.MOCK_BOOKMARKS[index],
        ...bookmarkData,
        updatedAt: new Date().toISOString()
      };
      return LOCAL_CONFIG.MOCK_BOOKMARKS[index];
    }
    throw new Error('Bookmark not found');
  }

  async deleteBookmark(id: string) {
    await this.delay(300);
    const index = LOCAL_CONFIG.MOCK_BOOKMARKS.findIndex(b => b.id === id);
    if (index !== -1) {
      LOCAL_CONFIG.MOCK_BOOKMARKS.splice(index, 1);
      return { success: true };
    }
    throw new Error('Bookmark not found');
  }

  async getFavorites() {
    await this.delay(300);
    return LOCAL_CONFIG.MOCK_BOOKMARKS.filter(bookmark => bookmark.isFavorite);
  }

  async toggleFavorite(id: string) {
    await this.delay(200);
    const bookmark = LOCAL_CONFIG.MOCK_BOOKMARKS.find(b => b.id === id);
    if (bookmark) {
      bookmark.isFavorite = !bookmark.isFavorite;
      return bookmark;
    }
    throw new Error('Bookmark not found');
  }

  async submitFeedback(feedbackData: any) {
    await this.delay(300);
    console.log('Mock feedback submitted:', feedbackData);
    return { success: true, message: 'Feedback submitted successfully' };
  }

  // Generic request method for compatibility
  async request(description: string, method: string, url: string, data: any = {}, options: any = {}) {
    console.log(`Mock API Request: ${method} ${url}`, data);
    
    // Route to appropriate mock method based on URL
    if (url.includes('/user')) {
      if (url.includes('/search')) {
        return this.searchUser(data.query || '');
      }
      return this.getCurrentUser();
    }
    
    if (url.includes('/bookmarks')) {
      if (method === 'GET') {
        return this.getBookmarks(data);
      } else if (method === 'POST') {
        return this.createBookmark(data);
      } else if (method === 'PUT') {
        return this.updateBookmark(data.id, data);
      } else if (method === 'DELETE') {
        return this.deleteBookmark(data.id);
      }
    }
    
    if (url.includes('/favorites')) {
      return this.getFavorites();
    }
    
    // Default response
    await this.delay(300);
    return { success: true, data: null };
  }

  // Convenience methods
  async get(url: string, data?: any, options?: any) {
    return this.request('', 'GET', url, data, options);
  }

  async post(url: string, data: any, options?: any) {
    return this.request('', 'POST', url, data, options);
  }

  async put(url: string, data: any, options?: any) {
    return this.request('', 'PUT', url, data, options);
  }

  async delete(url: string, data?: any, options?: any) {
    return this.request('', 'DELETE', url, data, options);
  }
}
